import { Directive, OnDestroy, OnInit, AfterViewInit, ElementRef, input, output, computed, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { delay, filter } from 'rxjs/operators';

@Directive({
  selector: '[observeVisibility]',
  standalone: true
})
export class ObserveVisibilityDirective implements OnDestroy, OnInit, AfterViewInit {
  debounceTime = input<number>(0); // in milliseconds
  threshold = input<number>(1); // value between 0 and 1
  rootMargin = input<number>(0); // value in pixels

  visible = output<HTMLElement>();

  private observer: IntersectionObserver | undefined;
  private subject$ = new Subject<{
    entry: IntersectionObserverEntry;
    observer: IntersectionObserver;
  }>();

  private element = inject(ElementRef);

  private observerOptions = computed(() => {
    const rootMargin = this.rootMargin().toString() + 'px';
    const threshold = this.threshold();

    return {
      rootMargin,
      threshold
    };
  });

  ngOnInit() {
    this.createObserver();
  }

  ngAfterViewInit() {
    this.startObservingElements();
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }

    this.subject$.next(null);
    this.subject$.complete();
  }

  private isVisible(element: HTMLElement): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      const observer = new IntersectionObserver(([entry]) => {
        resolve(entry.intersectionRatio === 1);
        observer.disconnect();
      });

      observer.observe(element);
    });
  }

  private createObserver() {
    const options = this.observerOptions();

    const isIntersecting = (entry: IntersectionObserverEntry) => entry.isIntersecting || entry.intersectionRatio > 0;

    this.observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (isIntersecting(entry)) {
          this.subject$.next({ entry, observer });
        }
      });
    }, options);
  }

  private startObservingElements() {
    if (!this.observer) {
      return;
    }

    this.observer.observe(this.element.nativeElement);

    const debounceTime = this.debounceTime();

    this.subject$.pipe(delay(debounceTime), filter(Boolean)).subscribe(async ({ entry, observer }) => {
      const target = entry.target as HTMLElement;
      const isStillVisible = await this.isVisible(target);

      if (isStillVisible) {
        this.visible.emit(target);
        observer.unobserve(target);
      }
    });
  }
}
